
import React, { useState, useContext, useEffect, useRef } from 'react'
import {
	Button,
	Card,
	CardHeader,
	CardFooter,
	Col,
	Container,
	Row,
	Table, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem
} from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { SpinnerComponent } from 'react-element-spinner'
import Paginator from '../../components/Paginator/Paginator'
import AuctionTemplatesService from '../../services/AuctionTemplatesService'
import SiteService from '../../services/SiteService'
import { TemplatesContext } from '../../contexts/TemplatesProvider'
import { SessionContext } from '../../contexts/SessionProvider'
import SelectVehicleModal from '../../components/Modals/SelectVehicleModal'
import {subscribe, unsubscribe} from '../../events/customEvents'
import AuctionsService from '../../services/AuctionsService'
import { AuctionsContext } from '../../contexts/AuctionsProvider'
import VehicleInspectionsService from '../../services/VehicleInspectionsService'
import { InspectionsContext } from '../../contexts/InspectionsProvider'
import PageTitle from '../../components/PageTitles/PageTitle'

const AuctionTemplatesListing = () =>
{
	const templatesContext = useContext(TemplatesContext)
	const [isBusy, setIsBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [pagingResult, setPagingResult] = useState({ pageCollection: [], totalRecords: 0 })
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [selectedAuctionTemplate, setSelectedAuctionTemplate] = useState({ adminAuctionId: undefined })
	const [showSelectVehicleModal, setShowSelectVehicleModal] = useState(false)
	const auctionsContext = useContext(AuctionsContext)
	const inspectionsContext = useContext(InspectionsContext)
	
	useEffect(() =>
	{
		retrievePagedTemplates(templatesContext.pagingRequest)
		subscribe('selectVehicleModalClosed', handleSelectVehicleModalClosed)
	}, [])
	
	useEffect(() =>
	{
		return () =>
		{
			unsubscribe('selectVehicleModalClosed', handleSelectVehicleModalClosed)
		}
	}, [])
	
	const retrievePagedTemplates = (pagingRequest) =>
	{
		setIsBusy(true)
		AuctionTemplatesService
			.getPagedTemplates(pagingRequest, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setPagingResult(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleTemplateSelected = (selectedAuctionTemplate) =>
	{
		templatesContext.setAuctionTemplate(selectedAuctionTemplate)
		
		retrieveTemplateTiers(selectedAuctionTemplate.adminAuctionId)
			.then(result =>
			{
				navigate('/secure/auction-template-detail')
			})
			.catch(error =>
			{
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveTemplateTiers = (auctionTemplateId) =>
	{
		return new Promise((resolve, reject) =>
		{
			setIsBusy(true)
			AuctionTemplatesService
				.getTemplateTiers(auctionTemplateId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					templatesContext.setAuctionTemplate((previousAuctionTemplate) =>
					{
						let currentAuctionTemplate = { ...previousAuctionTemplate }
						currentAuctionTemplate.auctionTemplateTiers = result.data.actionResult
						
						return currentAuctionTemplate
					})
					
					resolve()
				})
				.catch((error) =>
				{
					setIsBusy(false)
					reject(error)
				})
		})
	}
	
	const handlePageChange = (pageIndex) =>
	{
		templatesContext.setPagingRequest((previousPagingRequest) =>
		{
			let currentPagingRequest = { ...previousPagingRequest }
			currentPagingRequest.pageIndex = pageIndex
			
			retrievePagedTemplates(currentPagingRequest)
			
			return currentPagingRequest
		})
	}
	
	const handleCreateNew = () =>
	{
		templatesContext.setAuctionTemplate({
			adminAuctionId: 0,
			name: '',
			createdByUserId: 0,
			createdDate: undefined,
			userName: '',
			defaultForTypeId: 1,
			defaultForEntityId: undefined,
			auctionTemplateTiers: [],
			settlementPeriodHours: 2
		})
		
		navigate('/secure/auction-template-detail')
	}
	
	const handleSelectVehicle = (auctionTemplate) =>
	{
		setSelectedAuctionTemplate(auctionTemplate)
		setShowSelectVehicleModal(true)
	}
	
	const handleSelectVehicleModalClosed = (event) =>
	{
		setShowSelectVehicleModal(false)
		
		if (event.detail.vehicleId !== undefined)
		{
			setIsBusy(true)
			AuctionsService
				.addTemplateVehicleAuction(event.detail.adminAuctionId, event.detail.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					auctionsContext.setAuction(result.data.actionResult)
					retrieveInspection(result.data.actionResult.auctionId)
						.then(result =>
						{
							navigate('/secure/auction-detail')
						})
						.catch(error =>
						{
							SiteService.displayApiError(error)
						})
				})
				.catch((error) =>
				{
					setIsBusy(false)
					SiteService.displayApiError(error)
				})
		}
	}
	
	const retrieveInspection = (auctionId) =>
	{
		return new Promise((resolve, reject) =>
		{
			setIsBusy(true)
			VehicleInspectionsService
				.getVehicleInspection(auctionId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					inspectionsContext.setVehicleInspection(result.data.actionResult)
					
					resolve()
				})
				.catch((error) =>
				{
					setIsBusy(false)
					reject(error)
				})
		})
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitle
				heading={ t('Templates.AuctionListing.Title') }
				subheading=""
				icon="pe-7s-map-2 icon-gradient bg-premium-dark"
			/>
			<Container className="mt--6" fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader>
								<Row style={{ width: '100%' }}>
									<Col>
										<div className="text-right">
											<Button color="primary" size="sm" onClick={ () => handleCreateNew() }>
												{ t('Templates.AuctionListing.Create_New') }
											</Button>
										</div>
									</Col>
								</Row>
							</CardHeader>
							<div className="table-responsive" >
								<Table className="align-items-center table-flush">
									<thead className="thead-light">
									<tr>
										<th className="sort" data-sort="make" scope="col">{t('Templates.AuctionListing.Description')}</th>
										<th className="sort" data-sort="model" scope="col">{t('Templates.AuctionListing.CreatedBy')}</th>
										<th className="sort" data-sort="year" scope="col">{t('Templates.AuctionListing.CreatedDate')}</th>
										<th scope="col"/>
									</tr>
									</thead>
									<tbody className="list">
									{pagingResult.pageCollection.map((pageItem) => (
										<tr
											key={pageItem.adminAuctionId}
											style={{cursor: 'pointer'}}
										>
											<td onClick={() => handleTemplateSelected(pageItem)}>{pageItem.name}</td>
											<td onClick={() => handleTemplateSelected(pageItem)}>{pageItem.createdByUserName}</td>
											<td onClick={() => handleTemplateSelected(pageItem)}>{dayjs(pageItem.createdDate).format('MMM DD, YYYY')}</td>
											<td className="text-right">
												<UncontrolledDropdown>
													<DropdownToggle
														className="btn-icon-only text-light"
														color=""
														role="button"
														size="sm"
													>
														<i className="fas fa-ellipsis-v"/>
													</DropdownToggle>
													<DropdownMenu className="dropdown-menu-arrow" right>
														<DropdownItem
															onClick={() => handleSelectVehicle(pageItem)}
														>
															{t('Vehicles.Listing.ItemMenu_CreateAuction')}
														</DropdownItem>
													</DropdownMenu>
												</UncontrolledDropdown>
											</td>
										</tr>
									))}
									</tbody>
								</Table>
							</div>
							<CardFooter>
								{
									(pagingResult.pageCollection.length === 0) && (
										<Row style={{ marginBottom: '10px' }}>
											<Col md="12" style={{ textAlign: 'center' }}>
												<h5 className="mb-0">{t('Templates.AuctionListing.No_Results')}</h5>
											</Col>
										</Row>
									)
								}
								<div style={{ marginLeft: 'auto' }}>
									{
										(pagingResult.pageCollection.length > 0) && (
											<nav>
												<Paginator
													currentPage={templatesContext.pagingRequest.pageIndex}
													pages={parseInt(((pagingResult.totalRecords - 1) / templatesContext.pagingRequest.pageSize) + 1, 10)}
													selectPage={handlePageChange}
												/>
											</nav>
										)
									}
								</div>
							</CardFooter>
						</Card>
					</div>
				</Row>
			</Container>
			
			<SelectVehicleModal showSelectVehicleModal={ showSelectVehicleModal } adminAuctionId={ selectedAuctionTemplate.adminAuctionId } />
		</>
	)
}

export default AuctionTemplatesListing
